// Save as: src/TemplateBuilder/CanvasEditor.js
// Requires: npm install react-rnd
//
// Free-form pixel canvas: text blocks and table blocks can be dragged/resized
// anywhere on an A4-proportioned page. The locked header block at the top
// represents your existing <Header /> component and can't be moved or deleted,
// matching the rule that Header must never be touched when building documents.

import React, { useState, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../axios';
import DataCatalogPanel from './DataCatalogPanel';
import TableConfigModal from './TableConfigModal';

const PAGE_WIDTH = 794;  // A4 @ 96dpi
const PAGE_HEIGHT = 1123;

let nextId = 1;

const CanvasEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState({});
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editingTableId, setEditingTableId] = useState(null);
  const [templateName, setTemplateName] = useState('Untitled template');

  useEffect(() => {
    axios.get(`${window.backendUrl}/template-catalog`).then((res) => setCatalog(res.data));

    if (templateId) {
      axios.get(`${window.backendUrl}/templates/${templateId}`).then((res) => {
        setTemplateName(res.data.Name);
        setElements(res.data.SchemaJson.pages[0].elements.map((el) => ({ ...el, id: el.id ?? nextId++ })));
      });
    } else {
      setElements([{ id: nextId++, type: 'header', x: 0, y: 0, w: PAGE_WIDTH, h: 90, locked: true }]);
    }
  }, [templateId]);

  const addTextBlock = (content = 'New text') => {
    const id = nextId++;
    setElements((prev) => [...prev, { id, type: 'text', x: 60, y: 150, w: 260, h: 30, content }]);
    setSelectedId(id);
  };

  const addTableBlock = () => {
    const id = nextId++;
    setElements((prev) => [
      ...prev,
      { id, type: 'table', x: 40, y: 250, w: 500, h: 200, path: [], columns: [], filters: [] }
    ]);
    setEditingTableId(id);
  };

  const insertFieldToken = (fieldPath) => {
    const token = `{{${fieldPath}}}`;
    if (selectedId) {
      setElements((prev) =>
        prev.map((el) => (el.id === selectedId && el.type === 'text' ? { ...el, content: el.content + token } : el))
      );
    } else {
      addTextBlock(token);
    }
  };

  const updateElement = (id, patch) => {
    setElements((prev) => prev.map((el) => (el.id === id ? { ...el, ...patch } : el)));
  };

  const deleteElement = (id) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const saveTemplate = async () => {
    const schema = { pageSize: 'A4', pages: [{ elements }] };
    if (templateId) {
      await axios.put(`${window.backendUrl}/templates/${templateId}`, { name: templateName, schema });
      alert('Template saved');
    } else {
      const res = await axios.post(`${window.backendUrl}/templates`, { name: templateName, schema });
      navigate(`/template-builder/${res.data.templateId}`);
    }
  };

  const editingTable = elements.find((el) => el.id === editingTableId);

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      <div style={{ width: 260, borderRight: '1px solid #ddd', padding: 12, overflowY: 'auto' }}>
        <input
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          style={{ width: '100%', marginBottom: 12, fontWeight: 'bold' }}
        />
        <button onClick={() => addTextBlock()} style={{ width: '100%', marginBottom: 6 }}>+ Text block</button>
        <button onClick={addTableBlock} style={{ width: '100%', marginBottom: 12 }}>+ Table block</button>
        <button onClick={saveTemplate} style={{ width: '100%', marginBottom: 12 }}>Save template</button>
        <DataCatalogPanel catalog={catalog} onFieldClick={insertFieldToken} />
      </div>

      <div style={{ flex: 1, overflow: 'auto', background: '#e5e5e5', padding: 40 }}>
        <div
          style={{
            width: PAGE_WIDTH,
            height: PAGE_HEIGHT,
            background: 'white',
            position: 'relative',
            margin: '0 auto',
            boxShadow: '0 0 8px rgba(0,0,0,0.2)'
          }}
        >
          {elements.map((el) => (
            <Rnd
              key={el.id}
              size={{ width: el.w, height: el.h }}
              position={{ x: el.x, y: el.y }}
              disableDragging={el.locked}
              enableResizing={!el.locked}
              onDragStop={(e, d) => updateElement(el.id, { x: d.x, y: d.y })}
              onResizeStop={(e, dir, ref, delta, pos) =>
                updateElement(el.id, { w: parseInt(ref.style.width, 10), h: parseInt(ref.style.height, 10), ...pos })
              }
              onClick={() => setSelectedId(el.id)}
              onDoubleClick={() => el.type === 'table' && setEditingTableId(el.id)}
              style={{
                outline:
                  selectedId === el.id ? '2px solid #3b82f6' : el.locked ? '1px dashed #ccc' : '1px dashed transparent',
                background: el.type === 'table' ? 'rgba(59,130,246,0.05)' : 'transparent'
              }}
            >
              {el.type === 'header' && (
                <div style={{ padding: 8, color: '#999', fontSize: 12 }}>[Company letterhead - locked]</div>
              )}
              {el.type === 'text' && (
                <div
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={(e) => updateElement(el.id, { content: e.target.innerText })}
                  style={{ padding: 4, fontSize: 13, outline: 'none' }}
                >
                  {el.content}
                </div>
              )}
              {el.type === 'table' && (
                <div style={{ padding: 8, fontSize: 12, color: '#555' }}>
                  {el.path.length ? `Table: ${el.path.join(' \u2192 ')} (${el.columns.length} cols)` : 'Double-click to configure table'}
                </div>
              )}
              {!el.locked && (
                <button
                  onClick={() => deleteElement(el.id)}
                  style={{ position: 'absolute', top: -10, right: -10, cursor: 'pointer' }}
                >
                  ×
                </button>
              )}
            </Rnd>
          ))}
        </div>
      </div>

      {editingTable && (
        <TableConfigModal
          catalog={catalog}
          tableElement={editingTable}
          onSave={(patch) => {
            updateElement(editingTable.id, patch);
            setEditingTableId(null);
          }}
          onClose={() => setEditingTableId(null)}
        />
      )}
    </div>
  );
};

export default CanvasEditor;
